import { APIService } from '../../../lib/api/APIService';

// Every staff endpoint takes an optional `scope` (org:<id> |
// cohort:<id>:<year> | group:<id>); without one the server uses the
// viewer's default scope.
const scopedQuery = (params, scope) => {
  const query = new URLSearchParams(params);
  if (scope) query.set('scope', scope);
  return query.toString();
};

class TeacherAnalyticsAPI extends APIService {
  /**
   * { scope, period, period_label, window_days, daily, active_students } for
   * the period (today | 7d | 30d | all, as the Students list reads it;
   * window_days is null). active_students: students with at least one
   * completed item (a quiz question answered, a practice set or paper part,
   * a marked essay, an interview, a Mastery session, a case study or case
   * assessment). daily: [{ date, items }] per Europe/London date, only days
   * with any items, oldest first. There is no time-on-task figure.
   */
  async getOverview({ period, scope }) {
    return this.get(`/api/teacher-analytics/overview?${scopedQuery({ period }, scope)}`);
  }

  /**
   * Chat counts for the scope: students using chat, the median, active
   * plans, tasks done on time, and who has stopped. No message text.
   */
  async getChatSummary({ period, scope }) {
    return this.get(`/api/teacher-analytics/chat-summary?${scopedQuery({ period }, scope)}`);
  }

  /**
   * { scope, period, period_label, window_days, leaderboard } -- per student
   * user_id, name, email, items (completed in the period) and active_days
   * (London dates with any), most items first, then most active days, then
   * name, at most 20. Students who completed nothing are absent. The
   * Overview ranks from the Students list instead (lib/derive
   * engagedRanking), which has every student.
   */
  async getLeaderboard({ period, scope }) {
    return this.get(`/api/teacher-analytics/leaderboard?${scopedQuery({ period }, scope)}`);
  }

  /**
   * Full tracker usage (papers, quiz generator, reasoning, documents).
   *
   * Replaces the raw fetch that used to live in TeacherDashboardPage.js:550-573,
   * which hand-rolled its own bearer token and fell back to a hardcoded
   * production DigitalOcean URL. Going through APIService brings token refresh,
   * retry and timeout that the raw fetch did not have.
   *
   * Returns { scope, students, period, period_label }. A scope with no
   * students gets 200 with an empty array, not a 404. ever_active,
   * days_inactive, last_active_ever_at, last_activity_kind and the
   * tracker_docs counts are not filtered by the period.
   */
  async getTrackerDashboard({ period, scope }) {
    return this.get(`/api/tracker/teacher/dashboard?${scopedQuery({ period }, scope)}`);
  }

  /**
   * Tracker documents and comments for one student.
   *
   * Returns { tracker_id, documents, comments, student_id } where `documents`
   * is an object keyed by document type, not an array. Throws APIError with
   * status 403 when the viewer may not see this student -- callers must
   * render that as a first-class state rather than letting it surface as a
   * crash.
   *
   * Note: this is not read-only. The backend calls _ensure_tracker, which
   * INSERTs a student_trackers row when none exists, so do not prefetch it.
   */
  async getStudentTracker(studentId) {
    return this.get(`/api/tracker/student/${encodeURIComponent(studentId)}`);
  }

  /**
   * Post teacher feedback on a student, optionally on one piece of their
   * writing: `subject` is { subject_kind, subject_id } with subject_kind one
   * of tara_essay | lnat_essay | case_study, or omitted for a comment on the
   * student as a whole.
   *
   * Responds 201 with only { id, created_at, subject_kind, subject_id } --
   * not the comment text -- so callers build the optimistic row from what
   * they sent. Can 403 exactly like getStudentTracker; 400 when the text is
   * empty or over 5000 characters; 422 when the piece is not this student's.
   */
  async postStudentComment(studentId, commentText, subject = null) {
    const body = { comment_text: commentText };
    if (subject && subject.subject_kind && subject.subject_id) {
      body.subject_kind = subject.subject_kind;
      body.subject_id = subject.subject_id;
    }
    return this.post(
      `/api/tracker/student/${encodeURIComponent(studentId)}/comments`,
      body,
    );
  }
}

export const teacherAnalyticsAPI = new TeacherAnalyticsAPI();

